// src/router/index.js
import { createRouter, createWebHistory } from "vue-router";
import { auth } from "@/firebase";
import { adminGuard } from "./adminGuard";
import { teacherGuard } from "./teacherGuard";
import { onboardingGuard } from "./onboardingGuard";
import { isTestModeAllowed } from "@/config/testMode";
/* eslint-disable */

import routes from "./routes-manifest";

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  // Hash links (e.g. /privacy#lesson-requests from the /lessons form) scroll
  // to their target. The app scrolls an inner container (.main-content in
  // App.vue), not the window, so vue-router's `{ el }` positioning — which
  // uses window.scrollTo — cannot reach it; scrollIntoView can. The target
  // may render a tick after navigation, so retry briefly, then give up.
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return new Promise((resolve) => {
        let attempts = 0;
        const tryScroll = () => {
          const el = document.getElementById(to.hash.slice(1));
          if (el) {
            el.scrollIntoView({ block: "start" });
            resolve(false);
          } else if (attempts++ < 10) {
            setTimeout(tryScroll, 50);
          } else {
            resolve(false);
          }
        };
        tryScroll();
      });
    }
    if (savedPosition) return savedPosition;
    return { top: 0 };
  },
});

// Every route component below is lazy-loaded via import(). If Firebase Hosting's
// SPA catch-all rewrite serves index.html for a chunk that briefly doesn't exist
// (e.g. CDN edge propagation lag right after a deploy), the browser tries to
// execute HTML as JS. When that happens as a webpack chunk-load rejection, Vue
// Router surfaces it here; when it happens as a raw parse error (seen more often
// in Safari, which times requests differently around deploys), it surfaces as an
// uncaught SyntaxError instead — so both paths are handled. Recovery is a single
// hard reload of the intended URL; the sessionStorage flag stops a genuine repeat
// failure from reload-looping the tab.
const CHUNK_ERROR_PATTERN =
  /Loading chunk .* failed|Failed to fetch dynamically imported module|Unexpected token '<'/i;

const reloadOnChunkError = (message, targetPath) => {
  if (!CHUNK_ERROR_PATTERN.test(message || "")) return false;
  const reloadKey = `chunk-reload-${targetPath}`;
  if (sessionStorage.getItem(reloadKey)) return false;
  sessionStorage.setItem(reloadKey, "1");
  window.location.href = targetPath;
  return true;
};

router.onError((error, to) => {
  reloadOnChunkError(error?.message, to.fullPath);
});

window.addEventListener("error", (event) => {
  reloadOnChunkError(
    event?.message,
    window.location.pathname + window.location.search
  );
});

router.afterEach((to) => {
  sessionStorage.removeItem(`chunk-reload-${to.fullPath}`);
});

// Waits for Firebase Auth to finish restoring session from IndexedDB before
// the guard makes a redirect decision. Without this, hard refreshes on
// auth-gated routes redirect logged-in users to /login because
// auth.currentUser is null until the async restore completes.
const waitForAuth = () =>
  new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });
  });

// Auth guard with enhanced test mode support
router.beforeEach(async (to, from, next) => {
  document.title = to.meta.title
    ? `${to.meta.title} | Crescendo`
    : "Crescendo - Music Teacher Marketplace";

  // Enhanced Cypress test mode handling (build-gated; never active in production)
  if (isTestModeAllowed()) {
    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

    if (requiresAuth) {
      // Check mock auth state
      const mockUser = window.__getAuthState ? window.__getAuthState() : null;

      if (!mockUser) {
        // No mock user, redirect to login with redirect query
        next({
          path: "/login",
          query: { redirect: to.fullPath },
        });
        return;
      }
    }

    next();
    return;
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiresGuest = to.matched.some((record) => record.meta.requiresGuest);
  const currentUser = await waitForAuth();

  if (requiresGuest && currentUser) {
    next({ path: "/dashboard" });
  } else if (requiresAuth && !currentUser) {
    // Save the intended destination
    next({
      path: "/login",
      query: { redirect: to.fullPath },
    });
  } else {
    next();
  }
});

// Apply admin guard after the auth guard
router.beforeEach(adminGuard);

// Apply teacher guard for teacher-only routes (D-3b)
router.beforeEach(teacherGuard);

// Apply onboarding guard for student flow
router.beforeEach(onboardingGuard);

export default router;
